import React, { FC, useState } from "react";
import { LuPlus, LuSearch, LuX } from "react-icons/lu";
import { useDebounce } from "usehooks-ts";

import { LottieLoading } from "../../../../../../components/graphics/LottieLoading";
import { Button } from "../../../../../../components/newUI/button";
import { Input } from "../../../../../../components/newUI/input";
import { ContactNoteModel } from "../../../../../../factories/contacts/models/contact-note.models";
import { getLinkedFirm } from "../../../../../../sessionStorage/sessionStorage";
import {
  useDeleteContactNoteMutation,
  useGetContactCustomersNotesQuery,
} from "../../../../../../slices/ContactNotesSlice";
import { useDeleteWorkNoteMutation } from "../../../../../../slices/WorkNotesSlice";
import { IContactProfile, IWorkNote } from "../../../../../../types/types";
import {
  getNoteAuthorColor,
  getNoteAuthorName,
  NoteEmptyState,
  useCanEditNote,
  useNoteDeleteConfirm,
} from "../../../../components/notes";
import { NoteViewModal } from "../../../../components/NoteViewModal";
import { ContactNoteModal } from "../../../../modals/ContactNoteModal";
import ErrorPopup from "../../../../modals/ErrorPopup";
import { ContactNote } from "./ContactNote";

type Props = {
  contact: IContactProfile;
};

export const ContactNotes: FC<Props> = ({ contact }) => {
  const [search, setSearch] = useState("");
  const debounceSearch = useDebounce(search, 200);

  const {
    data: contactNotes,
    isFetching,
    refetch,
  } = useGetContactCustomersNotesQuery({
    orgId: getLinkedFirm()?.orgId,
    customerId: contact?.customerId,
    query: debounceSearch || "",
  });
  const [deleteContactNote] = useDeleteContactNoteMutation();
  const [deleteWorkNote] = useDeleteWorkNoteMutation();

  // The list mixes the client's own notes and its work items' notes; each
  // row's own id is `noteId`. Work notes are deleted through the work item.
  const deleteNote = async (note: ContactNoteModel) => {
    const id = note.noteId || note.id;
    try {
      if (note.workId) {
        await deleteWorkNote({ id, workId: note.workId } as IWorkNote).unwrap();
      } else {
        await deleteContactNote({ ...note, id }).unwrap();
      }
    } catch {
      setIsOpenError(true);
    }
    refetch();
  };
  const { requestDelete, confirmModal: confirmDeleteModal } =
    useNoteDeleteConfirm<ContactNoteModel>(deleteNote, (note) => note.title);

  const [isOpenAdd, setIsOpenAdd] = useState(false);
  const [isOpenError, setIsOpenError] = useState(false);
  const [currentNote, setCurrentNote] = useState<ContactNoteModel | null>(null);
  const [viewNote, setViewNote] = useState<ContactNoteModel | null>(null);
  const canEdit = useCanEditNote();
  const [isWork, setIsWork] = useState(true);

  const handleOpenAddModal = () => {
    setIsOpenAdd(true);
    setCurrentNote(null);
  };

  return (
    <>
      <div className={"w-full max-w-[860px] py-5"}>
        <div className={"mb-4 flex w-full items-center justify-between gap-4"}>
          <span className={"text-[13px] font-medium text-[#9097A8]"}>
            {contactNotes?.length || 0}{" "}
            {(contactNotes?.length || 0) === 1 ? "note" : "notes"}
          </span>
          <Button
            type={"button"}
            variant={"primary"}
            size={"md"}
            autoWidth
            leftIcon={<LuPlus size={16} strokeWidth={2.2} />}
            className={"h-10 px-[18px] text-[13.5px] font-bold"}
            onClick={handleOpenAddModal}>
            Add note
          </Button>
        </div>

        {(!!contactNotes?.length || !!search) && (
          <div className={"relative mb-4"}>
            <Input
              value={search}
              placeholder={"Search notes"}
              leftIcon={<LuSearch size={16} strokeWidth={2} />}
              className={"h-[42px] rounded-[9px] text-[13.5px]"}
              onChange={(e) => setSearch(e.target.value)}
              rightIcon={
                search ? (
                  <Button
                    type={"button"}
                    variant={"ghost"}
                    size={"xs"}
                    iconOnly
                    autoWidth
                    leftIcon={<LuX size={16} />}
                    className={"text-[#A6ACBB] hover:text-[#6B7185]"}
                    onClick={() => setSearch("")}
                  />
                ) : null
              }
            />
          </div>
        )}

        <div>
          {isFetching ? (
            <div className={"p-4"}>
              <LottieLoading />
            </div>
          ) : !contactNotes?.length ? (
            <NoteEmptyState
              variant={search ? "noResults" : "empty"}
              emptyDescription={"Notes added to this contact will appear here."}
            />
          ) : (
            <div className={"flex flex-col gap-3"}>
              {contactNotes?.map((note) => (
                <ContactNote
                  key={note?.noteId}
                  note={note}
                  setIsWork={setIsWork}
                  setCurrentNote={setCurrentNote}
                  setIsOpenAdd={setIsOpenAdd}
                  deleteContactNote={requestDelete}
                  onViewNote={setViewNote}
                />
              ))}
            </div>
          )}
        </div>
      </div>
      {isOpenAdd && (
        <ContactNoteModal
          isActive={isOpenAdd}
          isWork={isWork}
          onClose={() => setIsOpenAdd(false)}
          contactId={contact?.contactId || ""}
          defaultValues={currentNote || undefined}
        />
      )}
      {viewNote && (
        <NoteViewModal
          note={{
            id: viewNote.noteId || viewNote.id,
            title: viewNote.title,
            note: viewNote.note,
            createdAt: viewNote.createdAt,
            authorName: getNoteAuthorName(viewNote),
            authorColor: getNoteAuthorColor(viewNote),
          }}
          onClose={() => setViewNote(null)}
          onEdit={
            !canEdit(viewNote)
              ? undefined
              : () => {
                  setCurrentNote(viewNote);
                  setIsWork(!!viewNote.workName);
                  setViewNote(null);
                  setIsOpenAdd(true);
                }
          }
        />
      )}
      <ErrorPopup
        closePopup={() => setIsOpenError(false)}
        isOpen={isOpenError}
      />
      {confirmDeleteModal}
    </>
  );
};
